import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { Controller } from "react-hook-form";
import MultiSelect from "components/common/MultiSelect";
import IconButton from "components/common/IconButton";
import { Plus, Trash } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { handleAddPractitionerList } from "../config/preauthorizationSlice";
import DataTable from "react-data-table-component";
import { useAppContext } from "providers/AppProvider";
import { GetSpecialityPractitonerList } from "../config/preauthorizationApi";
import ConfirmationModal from "components/common/custom/confirmation-modal";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const PreauthorizationRequestPractitioner = ({
  control,
  setValue,
  watch,
  mergedSpecialities,
  setError,
  clearErrors,
}) => {
  const dispatch = useDispatch();
  const {
    practitionerListAdded,
    preauthorizationRequest,
    practitionerRoleList,
    practitionerList,
    viewPreauthorizationInfo,
  } = useSelector((state) => state.preauthorization);

  const [searchParams] = useSearchParams();
  const reuse = searchParams.get("reuse") === "true";
  const followUp = searchParams.get("followup") === "true";
  const edit = searchParams.get("edit") === "true";
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdatePractitioner = async (rowId, practitioner) => {
    try {
      setIsLoading(true);
      const response = await dispatch(
        GetSpecialityPractitonerList({
          practitioner_id: practitioner.practitioner_license,
        })
      ).unwrap();

      const specialties = response?.data?.speciality || [];

      let practice = null;
      if (specialties.length > 0) {
        const firstSpecialty = specialties[0];
        practice = {
          value: firstSpecialty.specialities_code,
          label: firstSpecialty.specialities_name.trim(),
        };
      }

      dispatch(
        handleAddPractitionerList({
          type: "update",
          payload: {
            id: rowId,
            practitioner: {
              value: practitioner,
              label: `(${practitioner.practitioner_license}) - ${practitioner.practitioner_title
                } ${practitioner.practitioner_firstname}${practitioner.practitioner_fathername
                  ? " " + practitioner.practitioner_fathername
                  : ""
                } ${practitioner.practitioner_familyname}`,
            },
            specialties: specialties,
            practice: practice,
          },
        })
      );
    } catch (error) {
      console.error("Error updating practitioner:", error);
      toast.error("Failed to update practitioner. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      dispatch(
        handleAddPractitionerList({
          type: "delete",
          payload: { id: deleteId },
        })
      );
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleUpdateField = (rowId, fieldName, value) => {
    dispatch(
      handleAddPractitionerList({
        type: "updateField",
        payload: {
          id: rowId,
          field: fieldName,
          value,
        },
      })
    );
  };

  const {
    config: { theme },
  } = useAppContext();

  // useEffect(() => {
  //   if (
  //     practitionerList?.length === 0 ||
  //     (practitionerListAdded?.length === 0 &&
  //       !!watch("healthcare_practitioner")?.value)
  //   ) {
  //     const primaryRole = practitionerRoleList?.find(
  //       (item) => item?.careroles_code?.toLowerCase() === "primary"
  //     );

  //     if (primaryRole) {
  //       setValue("practitioner_roles", primaryRole);
  //     }
  //   }
  // }, [practitionerRoleList, practitionerListAdded, setValue]);

  // Helper to fetch specialities
  const fetchSpecialities = async (practitioner_id) => {
    try {
      const response = await dispatch(
        GetSpecialityPractitonerList({
          practitioner_id,
        })
      ).unwrap();
      console.log({ response: response?.data?.speciality });
      return response?.data?.speciality || [];
    } catch (error) {
      console.error("Error fetching specialties:", error);
      toast.error("Failed to fetch specialties. Please try again.");
      return [];
    }
  };

  // Reusable add logic without calling API
  const dispatchAddPractitioner = ({
    practitioner,
    practice,
    role,
    specialties,
  }) => {
    // Validate primary role
    const primaryCount = practitionerListAdded.filter(
      (p) => p.role?.careroles_code?.toLowerCase() === "primary"
    ).length;
    if (
      primaryCount >= 1 &&
      role?.careroles_code?.toLowerCase() === "primary"
    ) {
      toast.error("Only one primary practitioner is allowed");
      return;
    }

    dispatch(
      handleAddPractitionerList({
        type: "add",
        payload: {
          practitioner,
          practice,
          role,
          specialties,
        },
      })
    );

    setValue("healthcare_practitioner", null);
    setValue("practices", null);
    setValue("practitioner_roles", null);
  };

  // Manual add function (e.g. from button click)
  const addPractitioner = async () => {
    const practitioner = watch("healthcare_practitioner");
    const practice = watch("practices");
    const role = watch("practitioner_roles");

    if (!practitioner) {
      setError("healthcare_practitioner", {
        type: "manual",
        message: "Practitioner is required",
      });
      return;
    }

    if (!practice) {
      setError("practices", {
        type: "manual",
        message: "Specialty is required",
      });
      return;
    }

    if (!role) {
      setError("practitioner_roles", {
        type: "manual",
        message: "Role is required",
      });
      return;
    }

    const specialties = await fetchSpecialities(
      practitioner.value.practitioner_license
    );

    dispatchAddPractitioner({
      practitioner,
      practice,
      role,
      specialties,
    });
  };

  // Process all practitioners from provider_request
  useEffect(() => {
    const fillAndAddPractitioners = async () => {
      // Exit early if conditions are not met
      if (
        (!reuse && !edit && !followUp) ||
        !viewPreauthorizationInfo?.provider_request ||
        !viewPreauthorizationInfo.provider_request.practitioner?.length ||
        practitionerListAdded?.length > 0 // Prevent re-adding if already populated
      ) {
        return;
      }

      setIsLoading(true);
      try {
        const preauthRequest = viewPreauthorizationInfo.provider_request;
        // Iterate over all practitioner entries
        for (const pract of preauthRequest.practitioner) {
          // Find the matching practitioner in practitionerList
          const matchedPractitioner = practitionerList.find(
            (p) => p.practitioner_id === pract.practitioner_id
          );

          if (!matchedPractitioner) {
            console.warn(
              `Practitioner with ID ${pract.practitioner_id} not found`
            );
            continue;
          }

          // Check for duplicates
          const isAlreadyAdded = practitionerListAdded?.some(
            (item) =>
              item?.practitioner?.value?.practitioner_id ===
              matchedPractitioner?.practitioner_id
          );

          if (isAlreadyAdded) {
            continue;
          }

          // Fetch specialties for the practitioner
          const specialties = await fetchSpecialities(
            matchedPractitioner.practitioner_license
          );

          // Set to first specialty since no specific qualification code is provided
          let matchedPractice = null;
          if (specialties.length > 0) {
            const selectedSpeciality = specialties?.find(spec => spec?.specialities_code == pract?.speciality)
            matchedPractice = {
              value: selectedSpeciality.specialities_code,
              label: selectedSpeciality.specialities_name.trim(),
            };
          }

          // Find the matching role
          const roleCode = pract.practitioner_role;
          const matchedRole = practitionerRoleList?.find(
            (r) => r.careroles_code?.toLowerCase() === roleCode?.toLowerCase()
          );

          console.log({ matchedPractitioner })

          // Prepare practitioner data
          const practitionerData = {
            practitioner: {
              value: matchedPractitioner,
              label: `(${matchedPractitioner.practitioner_license || ''}) - ${matchedPractitioner.practitioner_title || "Dr"
                } ${matchedPractitioner.practitioner_firstname || ''} ${matchedPractitioner.practitioner_fathername || ""
                } ${matchedPractitioner.practitioner_familyname || ''}`,
            },
            practice: matchedPractice,
            role: matchedRole
              ? {
                careroles_code: matchedRole.careroles_code,
                careroles_name: matchedRole.careroles_name,
              }
              : null,
            specialties,
          };

          // Only add if practitioner, role, and practice are valid
          if (
            practitionerData.practitioner &&
            practitionerData.role &&
            practitionerData.practice
          ) {
            dispatchAddPractitioner(practitionerData);
          } else {
            console.warn(
              `Skipping practitioner ${matchedPractitioner.practitioner_id}: Missing role or practice`
            );
          }
        }
      } catch (error) {
        console.error("Error processing practitioners:", error);
        toast.error("Failed to process practitioners. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fillAndAddPractitioners();
  }, [
    reuse,
    edit,
    followUp,
    practitionerList,
    searchParams,
    viewPreauthorizationInfo,
    practitionerRoleList,
    practitionerListAdded,
    dispatch,
    setValue,
  ]);

  console.log({ practitionerList });

  const columns = [
    {
      name: "Practitioner",
      cell: (row) => (
        <Form.Select
          className="fs-10"
          value={row?.practitioner?.value?.practitioner_id || ""}
          onChange={(e) => {
            const selected = practitionerList.find(
              (p) => p.practitioner_id == e.target.value
            );
            if (selected) handleUpdatePractitioner(row.id, selected);
          }}
          disabled={isLoading}
        >
          {practitionerList.map((p) => (
            <option key={p.practitioner_id} value={p.practitioner_id}>
              ({p.practitioner_license}) - {p.practitioner_title}{" "}
              {p.practitioner_firstname} {p.practitioner_fathername || ""}{" "}
              {p.practitioner_familyname || ""}
            </option>
          ))}
        </Form.Select>
      ),
    },
    {
      name: "Specialty",
      cell: (row) => (
        <Form.Select
          className="fs-10"
          value={row?.practice?.value || ""}
          onChange={(e) =>
            handleUpdateField(row.id, "practice", {
              value: e.target.value,
              label:
                row.specialties?.find(
                  (s) => s.specialities_code === e.target.value
                )?.specialities_name || "",
            })
          }
          disabled={isLoading}
        >
          <option value="">Select Practice</option>
          {row.specialties?.map((s) => (
            <option key={s.specialities_code} value={s.specialities_code}>
              {s.specialities_name}
            </option>
          ))}
        </Form.Select>
      ),
    },
    {
      name: "Role",
      cell: (row) => (
        <Form.Select
          className="fs-10"
          value={row?.role?.careroles_code || ""}
          onChange={(e) =>
            handleUpdateField(row.id, "role", {
              careroles_code: e.target.value,
              careroles_name:
                practitionerRoleList?.find(
                  (r) => r.careroles_code === e.target.value
                )?.careroles_name || "",
            })
          }
          disabled={isLoading}
        >
          <option value="">Select Role</option>
          {practitionerRoleList.map((role) => (
            <option key={role.careroles_code} value={role.careroles_code}>
              {role.careroles_name}
            </option>
          ))}
        </Form.Select>
      ),
    },
    {
      name: "Actions",
      button: true,
      cell: (row) => (
        <IconButton
          size="sm"
          variant="danger"
          onClick={() => handleDeleteClick(row.id)}
          disabled={isLoading}
        >
          <Trash />
        </IconButton>
      ),
    },
  ];

  return (
    <motion.div
      style={{ border: "2px solid #d9f8eb" }}
      className="rounded p-3 position-relative mt-3"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      transition={{ duration: 0.5 }}
    >
      <div className="position-absolute top-0 start-40 translate-middle-y px-2">
        <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
          <span className="fs-11">Practitioners</span>
        </span>
      </div>

      <Row>
        <Col xs={12} md={4}>
          <Form.Group controlId="healthcare_practitioner">
            <label className="form-label">
              Healthcare Practitioner <span className="text-danger">*</span>
            </label>
            <Controller
              name="healthcare_practitioner"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <MultiSelect
                  {...field}
                  isMulti={false}
                  menuPlacement="auto"
                  options={practitionerList?.map((p) => ({
                    value: p,
                    label: `(${p.practitioner_license}) - ${p.practitioner_title
                      } ${p.practitioner_firstname} ${p.practitioner_fathername || ""
                      } ${p.practitioner_familyname || ""}`,
                  }))}
                  placeholder="Select Practitioner"
                  isClearable={true}
                  value={field.value || null}
                  onChange={async (selected) => {
                    setValue("healthcare_practitioner", selected);
                    clearErrors("healthcare_practitioner");
                    if (selected) {
                      const specialties = await fetchSpecialities(
                        selected.value.practitioner_license
                      );
                      if (specialties.length > 0) {
                        const first = specialties[0];
                        setValue("practices", {
                          value: first.specialities_code,
                          label: first.specialities_name.trim(),
                        });
                        const primaryRole = practitionerRoleList?.find(
                          (item) =>
                            item?.careroles_code?.toLowerCase() === "primary"
                        );

                        if (
                          primaryRole &&
                          practitionerListAdded?.length === 0
                        ) {
                          console.log("tesrt");
                          setValue("practitioner_roles", primaryRole);
                        }
                      } else {
                        setValue("practices", null);
                      }
                    } else {
                      setValue("practices", null);
                    }
                  }}
                  isDisabled={isLoading}
                />
              )}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={4}>
          <Form.Group controlId="practices">
            <label className="form-label">
              Specialty <span className="text-danger">*</span>
            </label>
            <Controller
              name="practices"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <MultiSelect
                  {...field}
                  isMulti={false}
                  menuPlacement="auto"
                  options={mergedSpecialities?.map((s) => ({
                    value: s.specialities_code,
                    label: s.specialities_name.trim(),
                  }))}
                  isClearable={true}
                  placeholder="Select Practice"
                  value={field.value || null}
                  onChange={(selected) => {
                    setValue("practices", selected);
                    clearErrors("practices");
                  }}
                  isDisabled={isLoading}
                />
              )}
            />
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={4}>
          <Row className="align-items-center justify-content-center">
            <Col xs={9}>
              <Form.Group controlId="practitioner_roles">
                <label className="form-label">
                  Practitioner Role <span className="text-danger">*</span>
                </label>
                <Controller
                  name="practitioner_roles"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      isMulti={false}
                      menuPlacement="auto"
                      options={practitionerRoleList?.map((item) => ({
                        value: item.careroles_code,
                        label: item.careroles_name,
                      }))}
                      isClearable={true}
                      placeholder="Select Role"
                      value={
                        field.value
                          ? {
                            value: field.value.careroles_code,
                            label: field.value.careroles_name,
                          }
                          : null
                      }
                      onChange={(selected) => {
                        setValue(
                          "practitioner_roles",
                          selected
                            ? {
                              careroles_code: selected.value,
                              careroles_name: selected.label,
                            }
                            : null
                        );
                        clearErrors("practitioner_roles");
                      }}
                      isDisabled={isLoading}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col xs={3}>
              <IconButton
                size="sm"
                variant="success"
                className="mt-4"
                onClick={addPractitioner}
                disabled={isLoading}
              >
                <Plus size={20} />
              </IconButton>
            </Col>
          </Row>
        </Col>

        <Col xs={12} className="mt-3">
          <DataTable
            columns={columns}
            theme={theme}
            data={practitionerListAdded}
            responsive
            fixedHeader
          />
        </Col>
      </Row>

      <ConfirmationModal
        modal={showDeleteModal}
        setModal={setShowDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        description="Are you sure you want to delete this practitioner?"
      />
    </motion.div>
  );
};

export default PreauthorizationRequestPractitioner;
